import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { Permission, Workspace } from '@shared/domain/permissions'
import { sessionApi, type MeResponse, type SessionUser } from '@shared/api/session.api'
import { tokenProvider } from '@shared/api/auth/tokenProvider'
import type { LoginScope } from '@shared/api/auth/auth.api'
import { sessionFromLogin } from '@shared/api/auth/loginSession'
import { useOrgStore } from './org'
import { resetAllStores } from './resetAllStores'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<SessionUser | null>(null)
  const initialised = ref(false)

  const permissions = computed(() => new Set<string>(user.value?.permissions ?? []))
  const workspace = computed<Workspace | null>(() => user.value?.workspace ?? null)
  const isAuthenticated = computed(() => !!user.value)

  const can = (p: Permission) => permissions.value.has(p)
  const canAny = (ps: Permission[]) => ps.some(can)

  function applySession(me: MeResponse) {
    user.value = me.user
    useOrgStore().setOrganisation(me.organisation)
  }

  /** GET /me — restores the user and their organisation (mock and OIDC sessions). */
  async function loadSession() {
    applySession(await sessionApi.me())
  }

  let pending: Promise<void> | null = null
  /** Called by the auth guard: restores a session once per page load. */
  function ensureSession(): Promise<void> {
    if (initialised.value) return Promise.resolve()
    pending ??= (async () => {
      try {
        if (await tokenProvider().init()) await loadSession()
      } catch {
        user.value = null
      } finally {
        initialised.value = true
        pending = null
      }
    })()
    return pending
  }

  /**
   * Resolves null once signed in, or the organisations to choose from when the account is
   * linked to more than one; call again with the chosen scope to finish.
   */
  async function signInWithPassword(
    email: string,
    password: string,
    scope?: LoginScope,
  ): Promise<LoginScope[] | null> {
    const provider = tokenProvider()
    if (!provider.signInWithPassword)
      throw new Error(`${provider.kind} sign-in does not accept passwords`)
    const res = await provider.signInWithPassword({
      email,
      password,
      actorType: scope?.actorType,
      orgId: scope?.orgId,
    })
    if (res.scopeSelectionRequired) return res.availableScopes
    // mock personas carry an org activation status that the real login response lacks
    if (provider.kind === 'mock') await loadSession()
    else applySession(sessionFromLogin(res, scope))
    return null
  }

  async function logout() {
    resetAllStores(['auth'])
    user.value = null
    await tokenProvider().signOut()
  }

  /** Session expired mid-use: clear state without a redirect round-trip to the IdP. */
  function expire() {
    resetAllStores(['auth'])
    user.value = null
  }

  function $reset() {
    user.value = null
    initialised.value = false
  }

  return {
    user,
    initialised,
    workspace,
    isAuthenticated,
    can,
    canAny,
    loadSession,
    ensureSession,
    signInWithPassword,
    logout,
    expire,
    $reset,
  }
})
